import { forwardRef, useId, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Panel.module.css';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Panel title. Becomes the accessible name of the region. */
  title: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  /**
   * Fixed width from the layout scale. `inspector` = 400px, `palette` = 272px,
   * `side` = 368px. Omit to fill the parent.
   */
  width?: 'inspector' | 'palette' | 'side';
  /** The body scrolls on its own, so the header and footer stay put. */
  scrollBody?: boolean;
  children: ReactNode;
}

const WIDTH_VAR = {
  inspector: 'var(--c-panel-inspector)',
  palette: 'var(--c-panel-palette)',
  side: 'var(--c-panel-side)',
} as const;

/**
 * A labelled region with a sticky header: the inspector, the primitives palette,
 * a drill-down column. `section` + `aria-labelledby` so it is a landmark a
 * keyboard user can jump to, not an anonymous div.
 */
export const Panel = forwardRef<HTMLElement, PanelProps>(function Panel(
  { title, eyebrow, actions, footer, width, scrollBody = false, className, style, children, ...rest },
  ref,
) {
  const titleId = useId();
  const vars: StyleWithVars = {};
  if (width !== undefined) vars['--dpem-panel-w'] = WIDTH_VAR[width];

  return (
    <section
      {...rest}
      ref={ref}
      aria-labelledby={titleId}
      className={clsx(styles.panel, width !== undefined && styles.fixed, className)}
      style={mergeStyle(vars, style)}
    >
      <header className={styles.header}>
        <div className={styles.headerText}>
          {eyebrow !== undefined && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
        </div>
        {actions !== undefined && <div className={styles.actions}>{actions}</div>}
      </header>
      <div className={clsx(styles.body, scrollBody && styles.scroll)}>{children}</div>
      {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
    </section>
  );
});
